import { TabsContent } from '@radix-ui/react-tabs';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import StyledTable from '../styled/StyledTable';
import {
  Distributor,
  DISTRIBUTOR_COLUMNS,
} from '@/types/distributors/distributorColumnDef';
import {
  useGetDistributorsCircuits,
  useGetDistributorsCountries,
  useGetDistributorsNetworks,
  useGetMyDistributors,
  useRemoveDistributor,
} from '@/hooks/useDistributors';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import { useForm, useWatch } from 'react-hook-form';
import { LabelValue } from '@/types/global';
import { useEffect, useState } from 'react';
import StyledSelect from '../styled/StyledSelect';
import StyledInput from '../styled/StyledInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import NoDistributorFound from './NoDistributorFound';
import UpdateSharedCircuitsModal from './modals/UpdateSharedCircuitsModal';
import StyledButton from '../styled/StyledButton';
import { ArrowRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { PAGE_SIZE } from '@/constants/pageSize';
import { RowSelectionState } from '@tanstack/react-table';
import ShareIcon from '@/assets/icons/share-06.svg?react';
import ReceivedPartnershipRequests from './ReceivedPartnershipRequests';
import DistributorInfoModal from './modals/DistributorInfoModal';
import ModalDeletion from '../ui/ModalDeletion';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

const ListDistributors = ({
  setHasDistributors,
}: {
  setHasDistributors: (hasDistributors: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<{
    search: string;
    country?: LabelValue<string>;
    network?: LabelValue<string>;
    circuit?: LabelValue<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      country: undefined,
      network: undefined,
      circuit: undefined,
    },
  });

  const searchValue = useWatch({ control, name: 'search' });
  const debouncedSearch = useDebouncedSearch(searchValue);
  const countryValue = useWatch({ control, name: 'country' });
  const networkValue = useWatch({ control, name: 'network' });
  const circuitValue = useWatch({ control, name: 'circuit' });
  const filters = {
    name: debouncedSearch,
    country: countryValue?.value ?? '',
    networkId: networkValue?.value ?? '',
    circuitId: circuitValue?.value ?? '',
  };

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const { data: distributorsResponse, isSuccess } = useGetMyDistributors({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const { data: countriesOptions } = useGetDistributorsCountries((data) =>
    data?.map(({ country }) => ({ label: country, value: country })),
  );

  const { data: circuitsOptions } = useGetDistributorsCircuits((data) =>
    data?.map((circuit) => ({ label: circuit.name, value: circuit.id })),
  );

  const { data: networksOptions } = useGetDistributorsNetworks((data) =>
    data?.map((network) => ({ label: network.name, value: network.id })),
  );

  useEffect(() => {
    if (isSuccess) setHasDistributors(distributorsResponse.total > 0);
  }, [distributorsResponse?.total, isSuccess, setHasDistributors]);

  const [selectedDistributors, setSelectedDistributors] = useState<
    Distributor[]
  >([]);
  const [distributorToViewId, setDistributorToViewId] = useState<string | null>(
    null,
  );
  const [distributorToDeleteId, setDistributorToDeleteId] = useState<
    string | null
  >(null);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectedRows = isSuccess
    ? distributorsResponse.distributors.filter((distributor) =>
        Object.keys(rowSelection).includes(distributor.agencyId),
      )
    : [];

  const { mutateAsync: removeDistributor } = useRemoveDistributor();
  const { toastAsync } = useToast();

  return (
    <div className="flex flex-col w-full h-auto p-6 gap-6">
      {isSuccess &&
        distributorsResponse.total > 0 &&
        distributorsResponse.countNewAgencies > 1 && (
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 p-6 rounded-lg bg-brand-100">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-semibold text-brand-700">
                {t('distributors.discoverNewDistributors')}
              </h1>
              <span className="text-base font-normal text-brand-700">
                {t('distributors.goodNews')}{' '}
                <span className="font-semibold">
                  {distributorsResponse.countNewAgencies}{' '}
                  {t('distributors.newDistributorsAvailable')}
                </span>
              </span>
            </div>
            <StyledButton
              title={t('distributors.viewNewDistributors')}
              className="px-3 py-2"
              icon={<ArrowRightIcon className="size-5" />}
              iconPosition="right"
              onClick={() => {
                navigate(PATHS.home.distributors.add, {
                  state: {
                    currentTab: 'recentlyAddedDistributors',
                  },
                });
              }}
            />
          </div>
        )}
      <Tabs defaultValue="myDistributors" className="w-full">
        <div className="flex w-full justify-between">
          <TabsList>
            <TabsTrigger
              value="myDistributors"
              className="hover:cursor-pointer"
            >
              {t('distributors.myDistributors')}
            </TabsTrigger>
            {isSuccess && distributorsResponse.countPartnerships > 0 && (
              <TabsTrigger
                value="partnershipRequests"
                className="hover:cursor-pointer flex items-center justify-center gap-2"
              >
                <span>{t('distributors.partnershipRequestsTitle')}</span>
                <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                  {distributorsResponse.countPartnerships}
                </div>
              </TabsTrigger>
            )}
          </TabsList>
          {selectedRows.length > 0 && (
            <StyledButton
              title={`${t('distributors.modifySharedCircuits')} (${selectedRows.length})`}
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => setSelectedDistributors(selectedRows)}
              icon={<ShareIcon className="size-5 stroke-brand-600" />}
              variant="outlined"
            />
          )}
        </div>
        <TabsContent value="myDistributors">
          <div className="flex flex-col gap-6 w-full h-auto pt-4">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-normal text-gray-600">
                {t('distributors.filterBy')}
              </span>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={networksOptions ?? []}
                    name="network"
                    isClearable={true}
                    placeholder={t('distributors.networkPlaceholder')}
                  />
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={countriesOptions ?? []}
                    name="country"
                    placeholder={t('distributors.columns.country')}
                    isClearable={true}
                  />
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={circuitsOptions ?? []}
                    name="circuit"
                    placeholder={t('circuits.title')}
                    isClearable={true}
                  />
                </div>
                <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
                  <StyledInput
                    control={control}
                    name="search"
                    placeholder={t('distributors.quickSearchPlaceholder')}
                    icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
                  />
                </div>
              </div>
            </div>
            {!isSuccess || distributorsResponse.total === 0 ? (
              <NoDistributorFound />
            ) : (
              <>
                <StyledTable
                  columns={getTranslatedConst(DISTRIBUTOR_COLUMNS)}
                  data={isSuccess ? distributorsResponse.distributors : []}
                  sorting={sorting}
                  setSorting={setSorting}
                  getRowId={(row) => row.agencyId}
                  onShareRow={(row) => {
                    setSelectedDistributors([row.original]);
                  }}
                  onViewRow={(row) => {
                    setDistributorToViewId(row.original.agencyId);
                  }}
                  setSelection={setRowSelection}
                  onDeleteRow={(row) => {
                    setDistributorToDeleteId(row.original.agencyId);
                  }}
                />

                {isSuccess && (
                  <StyledNextPreviousButtons
                    page={page}
                    pageSize={PAGE_SIZE}
                    setPage={setPage}
                    dataLength={distributorsResponse.total}
                    label={t('distributors.distributor')}
                  />
                )}
              </>
            )}
          </div>
        </TabsContent>
        <TabsContent value="partnershipRequests">
          <ReceivedPartnershipRequests />
        </TabsContent>
      </Tabs>
      {selectedDistributors.length > 0 && (
        <UpdateSharedCircuitsModal
          distributors={selectedDistributors}
          closeModal={() => {
            setSelectedDistributors([]);
          }}
        />
      )}
      {distributorToViewId && (
        <DistributorInfoModal
          distributorId={distributorToViewId}
          closeModal={() => setDistributorToViewId(null)}
        />
      )}
      {distributorToDeleteId && (
        <ModalDeletion
          actionOnClick={() => {
            toastAsync(removeDistributor(distributorToDeleteId), {
              success: t('modal.deletion.message.single.distributeur'),
              error: t('distributors.distributorDeletionError'),
            });
          }}
          closeModal={() => setDistributorToDeleteId(null)}
          type="distributeur"
        />
      )}
    </div>
  );
};

export default ListDistributors;
